import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { uniqueId } from "@faire/web--source/common/server-side-rendering/uniqueId";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { trackRetailerMobileAppsflyerQrcodeFailed } from "@faire/web-api--source/events/retailerMobile/actionCategoryOther/appsflyerQrcodeFailed";
import React, { useState, FC, useEffect } from "react";
import styled, { css } from "styled-components";

interface IProps {
  fallbackQrCodeUrl?: string;
  qrCodeWidth: number;
}

export const AppsFlyerQRCode: FC<IProps> = ({
  fallbackQrCodeUrl,
  qrCodeWidth,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [showAppsFlyerQRCode, setShowAppsFlyerQRCode] = useState(true);
  const QR_CODE_ID = uniqueId("appsflyer_qr_code");

  useEffect(() => {
    // AF_SMART_SCRIPT is a script added to Google Tag Manager on prod
    // Manually inject script to test on local/staging
    // doc: https://dev.appsflyer.com/hc/docs/onelink-smart-script-v2web-to-app-url-generator#create-a-qr-code-with-the-smart-script-result
    // script: https://tagmanager.google.com/?authuser=1#/container/accounts/1798415649/containers/7538127/workspaces/1000085/tags/140
    const displayQrCode = getWindow()?.AF_SMART_SCRIPT?.displayQrCode;
    if (displayQrCode) {
      displayQrCode(QR_CODE_ID);
    } else {
      setShowAppsFlyerQRCode(false);
      trackRetailerMobileAppsflyerQrcodeFailed();
    }
  }, [QR_CODE_ID]);

  const isSSR = useIsSSR();

  return (
    <QrCodeWrapper id={QR_CODE_ID} $qrCodeWidth={qrCodeWidth}>
      {showAppsFlyerQRCode ? null : (
        <FallbackQRCodeImage
          src={optimizeImageUrl({ url: fallbackQrCodeUrl }, isSSR, {
            height: qrCodeWidth,
            width: qrCodeWidth,
          })}
          alt={strictLocalize({
            defaultMessage: "QR code linking to an app store",
            description: {
              text: "Alt text for a QR code image linking to an app store",
              img: "https://cdn.faire.com/fastly/aacb3261706fd76b7288bb0596397b81c74108054373e7e547cb1f1be36f8f12.png",
            },
          })}
        />
      )}
    </QrCodeWrapper>
  );
};

const FallbackQRCodeImage = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const QrCodeWrapper = styled.div<{
  $qrCodeWidth: number;
}>`
  ${({ $qrCodeWidth }) => css`
    max-width: ${getSpacing($qrCodeWidth)};
    > canvas {
      width: ${getSpacing($qrCodeWidth)};
    }
  `}
`;
